import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  useAuthenticate,
  useApprovedDrones,
  useGetOrganizationId,
  useEncryptedToggle,
} from "../cacheQueries";
import { buildApprovedDroneLookup } from "../utils/detectionAlertContract";
import {
  useEncryptedRingStore,
  RING_STALE_MS,
  RING_PRUNE_INTERVAL_MS,
} from "../stores/encryptedRingStore";
import { isEncryptedLiveEnabled } from "./encryptedRing";
import { scanningClient } from "./scanningClient";

// Mounted once at app root next to <RemarksSseListener/>. Owns the React-bound
// inputs (auth, org, approved drones, route gate) and pushes them into the
// scanningClient singleton.
export default function ScanningProvider() {
  const { data: user, isLoading: userLoading } = useAuthenticate();
  const { data: organizationId } = useGetOrganizationId();
  const { data: approvedDrones = [] } = useApprovedDrones();
  const location = useLocation();

  const approvedDronesBySerial = useMemo(
    () => buildApprovedDroneLookup(approvedDrones),
    [approvedDrones],
  );

  useEffect(() => {
    scanningClient.setApprovedDrones(approvedDronesBySerial);
  }, [approvedDronesBySerial]);

  // The org's Encrypted Detection setting, kept resident for every scanning
  // route: scanningClient reads the ["encryptedToggle", orgId] entry per
  // frame (fail-closed while absent), and turning the setting off clears the
  // rings already on screen — the gateway stops sending within 30 s but never
  // sends Lost frames for rings it no longer delivers.
  const { data: encryptedToggle } = useEncryptedToggle();
  const encryptedLiveEnabled = isEncryptedLiveEnabled(encryptedToggle);
  useEffect(() => {
    if (!encryptedLiveEnabled) useEncryptedRingStore.getState().reset();
  }, [encryptedLiveEnabled]);

  // Stale-ring safety net on every scanning route (it used to live in the Map
  // page's ring hook, so a ring could outlive its drone on the Dashboard).
  const pruneStale = useEncryptedRingStore((s) => s.pruneStale);
  useEffect(() => {
    const id = window.setInterval(
      () => pruneStale(Date.now(), RING_STALE_MS),
      RING_PRUNE_INTERVAL_MS,
    );
    return () => window.clearInterval(id);
  }, [pruneStale]);

  useEffect(() => {
    // Normalize a trailing slash so "/map/" is treated as "/map". An exact
    // match dropped the scanning WS (scanningClient.stop → no live drones) on
    // any slashed URL, e.g. "/map/?liveSymbols=1".
    const path = location.pathname.replace(/\/+$/, "") || "/";
    const isScanningRoute = path === "/" || path === "/map";
    const shouldRun = !userLoading && !!user && organizationId != null && isScanningRoute;

    if (!shouldRun) {
      scanningClient.stop();
      return;
    }

    scanningClient.start({ orgId: organizationId });
  }, [userLoading, user, organizationId, location.pathname]);

  return null;
}
